import { useState, useRef } from "react";
import axios from "axios";
import { X, Loader2, Upload, CheckCircle, Image } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Exchange rate
const USD_TO_HTG = 148;

export const PaymentModal = ({ order, onClose, onSuccess }) => {
  const [selectedMethod, setSelectedMethod] = useState("moncash");
  const [loading, setLoading] = useState(false);
  const [paymentInstructions, setPaymentInstructions] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [proofUploaded, setProofUploaded] = useState(false);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('enterprise_token');

  // Calculate amounts
  const totalUSD = order?.subtotal || 0;
  const totalHTG = order?.total_htg || Math.round(totalUSD * USD_TO_HTG);

  const handlePayment = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/enterprise/orders/${order.id}/pay`,
        { method: selectedMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        if (selectedMethod === "moncash" && response.data.redirect_url) {
          toast.success("Redireksyon nan MonCash...");
          if (onSuccess) onSuccess();
          window.location.href = response.data.redirect_url;
        } else {
          // Show instructions for Virement/Zelle
          setPaymentInstructions(response.data.instructions);
          toast.success("Tanpri swiv enstriksyon yo");
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : "Erè nan peman an");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error("Sèlman imaj JPG, PNG, WEBP aksepte");
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Imaj la twò gwo. Maksimòm 5MB");
        return;
      }
      
      setProofFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProofPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadProof = async () => {
    if (!proofFile) {
      toast.error("Tanpri chwazi yon imaj");
      return;
    }
    
    setUploadingProof(true);
    try {
      const formData = new FormData();
      formData.append('file', proofFile);
      
      const response = await axios.post(
        `${API}/enterprise/orders/${order.id}/upload-proof`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`
          } 
        }
      );
      
      if (response.data.success) {
        setProofUploaded(true);
        toast.success("Prèv peman telechaje avèk siksè!");
      }
    } catch (error) {
      console.error("Upload error:", error);
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : "Erè nan telechaje imaj la");
    } finally {
      setUploadingProof(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Kopye!");
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-[#0f1419] rounded-2xl w-full max-w-3xl overflow-hidden border border-gray-700 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white z-10"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="flex flex-col md:flex-row">
          {/* Left side - Amount */}
          <div className="p-8 md:w-2/5 flex flex-col justify-center">
            <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">TOTAL PEMAN</p>
            <p className="text-4xl font-bold text-white mb-2">
              {totalUSD.toFixed(2)} <span className="text-2xl">USD</span>
            </p>
            <p className="text-[#4ADE80] text-sm">
              Konvèsyon MonCash: {totalHTG.toLocaleString()} HTG (Rate: {USD_TO_HTG})
            </p>
          </div>

          {/* Right side - Payment methods */}
          <div className="p-8 md:w-3/5 bg-[#1a1f2e] rounded-l-3xl">
            {!paymentInstructions ? (
              <>
                <h3 className="text-white font-bold text-lg mb-6">Chwazi Mwayen Peman:</h3>
                
                <div className="space-y-4 mb-6">
                  {/* MonCash Option */}
                  <label 
                    className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedMethod === 'moncash' 
                        ? 'border-[#4ADE80] bg-[#4ADE80]/10' 
                        : 'border-gray-600 hover:border-gray-500'
                    }`}
                    data-testid="payment-option-moncash"
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="moncash"
                      checked={selectedMethod === 'moncash'}
                      onChange={() => setSelectedMethod('moncash')}
                      className="w-5 h-5 text-[#4ADE80] border-gray-500 focus:ring-[#4ADE80]"
                    />
                    <div className="ml-4">
                      <p className="text-white font-medium">MonCash (Haiti)</p>
                      <p className="text-gray-400 text-sm">{totalHTG.toLocaleString()} HTG</p>
                    </div>
                  </label>

                  {/* Zelle Option */}
                  <label 
                    className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedMethod === 'zelle' 
                        ? 'border-[#4ADE80] bg-[#4ADE80]/10' 
                        : 'border-gray-600 hover:border-gray-500'
                    }`}
                    data-testid="payment-option-zelle"
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="zelle"
                      checked={selectedMethod === 'zelle'}
                      onChange={() => setSelectedMethod('zelle')}
                      className="w-5 h-5 text-[#4ADE80] border-gray-500 focus:ring-[#4ADE80]"
                    />
                    <div className="ml-4">
                      <p className="text-white font-medium">Zelle (US)</p>
                      <p className="text-gray-400 text-sm">{totalUSD.toFixed(2)} USD</p>
                    </div>
                  </label>

                  {/* Virement Option */}
                  <label 
                    className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedMethod === 'virement' 
                        ? 'border-[#4ADE80] bg-[#4ADE80]/10' 
                        : 'border-gray-600 hover:border-gray-500'
                    }`}
                    data-testid="payment-option-virement"
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="virement"
                      checked={selectedMethod === 'virement'}
                      onChange={() => setSelectedMethod('virement')}
                      className="w-5 h-5 text-[#4ADE80] border-gray-500 focus:ring-[#4ADE80]"
                    />
                    <div className="ml-4">
                      <p className="text-white font-medium">Virement Bancaire</p>
                      <p className="text-gray-400 text-sm">{totalHTG.toLocaleString()} HTG</p>
                    </div>
                  </label>
                </div>

                {/* Pay Button */}
                <button
                  onClick={handlePayment}
                  disabled={loading}
                  className="w-full py-4 bg-[#4ADE80] hover:bg-[#3FCF70] text-black font-bold text-lg rounded-xl transition-all disabled:opacity-50"
                  data-testid="pay-order-btn"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Chajman...
                    </span>
                  ) : (
                    "Peye Kòmand Lan"
                  )}
                </button>
              </>
            ) : (
              /* Payment Instructions */
              <div>
                <h3 className="text-white font-bold text-lg mb-4">
                  Enstriksyon {selectedMethod === 'virement' ? 'Virement' : 'Zelle'}
                </h3>
                
                <div className="bg-[#0f1419] rounded-xl p-4 space-y-3 mb-6">
                  {selectedMethod === 'virement' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Bank:</span>
                        <span className="text-white font-medium">{paymentInstructions.bank_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Non Kont:</span>
                        <span className="text-white font-medium">{paymentInstructions.account_name}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Nimewo Kont:</span>
                        <button 
                          onClick={() => copyToClipboard(paymentInstructions.account_number)}
                          className="text-[#4ADE80] font-mono hover:underline"
                        >
                          {paymentInstructions.account_number}
                        </button>
                      </div>
                    </>
                  )}
                  
                  {selectedMethod === 'zelle' && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Imèl Zelle:</span>
                      <button 
                        onClick={() => copyToClipboard(paymentInstructions.email)}
                        className="text-[#4ADE80] hover:underline"
                      >
                        {paymentInstructions.email}
                      </button>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center bg-yellow-500/20 p-2 rounded">
                    <span className="text-yellow-400">Referans (Memo):</span>
                    <button 
                      onClick={() => copyToClipboard(paymentInstructions.reference)}
                      className="text-yellow-400 font-mono font-bold hover:underline"
                    >
                      {paymentInstructions.reference}
                    </button>
                  </div>
                  
                  <div className="flex justify-between pt-2 border-t border-gray-700">
                    <span className="text-white font-bold">Montan:</span>
                    <span className="text-[#4ADE80] font-bold text-xl">
                      {selectedMethod === 'zelle' 
                        ? `${paymentInstructions.amount_usd?.toFixed(2)} USD`
                        : `${paymentInstructions.amount_htg?.toLocaleString()} HTG`
                      }
                    </span>
                  </div>
                </div>

                {/* Image Upload Section for Zelle */}
                {selectedMethod === 'zelle' && (
                  <div className="mb-6">
                    <h4 className="text-white font-medium mb-3">
                      <Image className="w-5 h-5 inline mr-2" />
                      Upload Prèv Peman (Screenshot Zelle)
                    </h4>
                    
                    {!proofUploaded ? (
                      <>
                        {/* Hidden file input */}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          accept="image/jpeg,image/png,image/jpg,image/webp"
                          className="hidden"
                        />
                        
                        {/* Upload area */}
                        <div 
                          onClick={() => fileInputRef.current?.click()}
                          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                            proofPreview 
                              ? 'border-[#4ADE80] bg-[#4ADE80]/5' 
                              : 'border-gray-600 hover:border-gray-500'
                          }`}
                        >
                          {proofPreview ? (
                            <div className="space-y-3">
                              <img 
                                src={proofPreview} 
                                alt="Preview" 
                                className="max-h-32 mx-auto rounded-lg"
                              />
                              <p className="text-gray-400 text-sm">{proofFile?.name}</p>
                              <p className="text-[#4ADE80] text-sm">Klike pou chanje imaj</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Upload className="w-10 h-10 mx-auto text-gray-500" />
                              <p className="text-gray-400">Klike pou chwazi imaj</p>
                              <p className="text-gray-500 text-sm">JPG, PNG oswa WEBP (Max 5MB)</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Upload button */}
                        {proofFile && (
                          <button
                            onClick={handleUploadProof}
                            disabled={uploadingProof}
                            className="w-full mt-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl transition-all disabled:opacity-50"
                            data-testid="upload-proof-btn"
                          >
                            {uploadingProof ? (
                              <span className="flex items-center justify-center gap-2">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Ap telechaje...
                              </span>
                            ) : (
                              <span className="flex items-center justify-center gap-2">
                                <Upload className="w-5 h-5" />
                                Telechaje Prèv Peman
                              </span>
                            )}
                          </button>
                        )}
                      </>
                    ) : (
                      /* Upload Success */
                      <div className="bg-green-500/20 border border-green-500 rounded-xl p-4 flex items-center gap-3">
                        <CheckCircle className="w-8 h-8 text-green-400" />
                        <div>
                          <p className="text-green-400 font-bold">Prèv Telechaje!</p>
                          <p className="text-green-300 text-sm">N ap verifye peman ou a</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <p className="text-yellow-400 text-sm mb-4">
                  ⚠️ Enpòtan: Mete nimewo kòmand la kòm referans pou nou ka idantifye peman ou a.
                </p>
                
                <button
                  onClick={() => {
                    if (onSuccess) onSuccess();
                    onClose();
                  }}
                  className="w-full py-4 bg-gray-600 hover:bg-gray-500 text-white font-bold rounded-xl transition-all"
                  data-testid="close-instructions-btn"
                >
                  Fèmen
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

